import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SeatingMove, SeatingState } from '@peles/shared';
import * as seatingApi from '../api/seating';

/**
 * An event's seating on one plan. Kept under `['floor-plans', …]` so every
 * floor-plan write that already refreshes plans (a save, an assignment,
 * auto-assign) refreshes who sits where with them.
 */
export const seatingKey = (eventId?: string, floorPlanId?: string | null) =>
  ['floor-plans', 'seating', eventId, floorPlanId ?? 'primary'] as const;

export function useSeatingState(eventId?: string, floorPlanId?: string | null) {
  return useQuery<SeatingState>({
    queryKey: seatingKey(eventId, floorPlanId),
    queryFn: () => seatingApi.getSeating(eventId!, floorPlanId ?? undefined),
    enabled: !!eventId,
    // An event with no plan answers 404; asking again will not draw one.
    retry: (count, err) =>
      (err as { response?: { status?: number } })?.response?.status !== 404 && count < 3,
  });
}

/** Put a seating answer in the cache and refresh what reads the guests' seats. */
function useSettle(eventId?: string) {
  const queryClient = useQueryClient();
  return (state: SeatingState) => {
    queryClient.setQueryData(seatingKey(eventId, state.plan.id), state);
    queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
    queryClient.invalidateQueries({ queryKey: ['guests'] });
  };
}

export function useMoveSeats(eventId?: string) {
  const settle = useSettle(eventId);
  return useMutation({
    mutationFn: (body: {
      floorPlanId?: string;
      moves: SeatingMove[];
      allowOverCapacity?: boolean;
    }) => seatingApi.moveSeats(eventId!, body),
    onSuccess: settle,
  });
}

export function useLockSeating(eventId?: string) {
  const settle = useSettle(eventId);
  return useMutation({
    mutationFn: ({ floorPlanId, locked }: { floorPlanId: string; locked: boolean }) =>
      seatingApi.lockSeating(eventId!, floorPlanId, locked),
    onSuccess: settle,
  });
}

export function useSetPrimarySeating(eventId?: string) {
  const settle = useSettle(eventId);
  return useMutation({
    mutationFn: (floorPlanId: string) => seatingApi.setPrimarySeating(eventId!, floorPlanId),
    onSuccess: settle,
  });
}

export function useArrangeSeats(eventId?: string) {
  const settle = useSettle(eventId);
  return useMutation({
    mutationFn: ({
      floorPlanId,
      arrangement,
    }: {
      floorPlanId: string;
      arrangement: seatingApi.SeatingArrangement;
    }) => seatingApi.arrangeSeats(eventId!, floorPlanId, arrangement),
    onSuccess: settle,
  });
}

export function usePinSeats(eventId?: string) {
  const settle = useSettle(eventId);
  return useMutation({
    mutationFn: ({
      floorPlanId,
      guestIds,
      locked,
    }: {
      floorPlanId: string;
      guestIds: string[];
      locked: boolean;
    }) => seatingApi.pinSeats(eventId!, floorPlanId, guestIds, locked),
    onSuccess: settle,
  });
}
